import React from 'react';
import { Shield, Lock, Eye, FileText, CheckCircle } from 'lucide-react';

const POPIA = () => {
  const popiaPrinciples = [
    {
      icon: Shield,
      title: 'Accountability',
      description: 'We are responsible for ensuring compliance with POPIA and protecting your personal information.'
    },
    {
      icon: Lock,
      title: 'Processing Limitation',
      description: 'We only collect and process personal information for legitimate purposes and with your consent.'
    },
    {
      icon: Eye,
      title: 'Purpose Specification',
      description: 'We clearly specify the purpose for collecting your personal information and use it only for that purpose.'
    },
    {
      icon: FileText,
      title: 'Information Quality',
      description: 'We ensure that the personal information we collect is accurate, complete, and up-to-date.'
    },
    {
      icon: CheckCircle,
      title: 'Security Safeguards',
      description: 'We implement appropriate security measures to protect your personal information from unauthorized access.'
    }
  ];

  const yourRights = [
    'Right to be informed about the collection and use of your personal information',
    'Right to access your personal information held by us',
    'Right to request correction of inaccurate or incomplete information',
    'Right to request deletion of your personal information',
    'Right to object to the processing of your personal information',
    'Right to withdraw consent at any time',
    'Right to lodge a complaint with the Information Regulator'
  ];

  const dataWeCollect = [
    'Name and contact information (email, phone number)',
    'Address and location information',
    'Health information (blood type, medical history for donors)',
    'Donation and volunteer activity records',
    'Communication preferences and interests',
    'Website usage data and cookies'
  ];

  const howWeUseData = [
    'To provide blood donation services and match donors with recipients',
    'To organize health camps and community programs',
    'To communicate with you about our services and events',
    'To process donations and provide receipts',
    'To improve our services and website functionality',
    'To comply with legal and regulatory requirements'
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-800 to-green-800 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <img 
              src="/aim-logo.png" 
              alt="AIM Foundation Logo" 
              className="w-20 h-20 md:w-24 md:h-24 object-contain"
            />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            POPIA Compliance
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Your privacy and data protection rights under South African law
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Overview */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Protection of Personal Information Act (POPIA)
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              AIM Foundation is committed to protecting your personal information in accordance with the Protection of Personal Information Act (POPIA) of South Africa. This page explains how we collect, use, and protect your data.
            </p>
          </div>
        </div>

        {/* POPIA Principles */}
        <section className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Our POPIA Compliance Principles
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {popiaPrinciples.map((principle, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg">
                <div className="flex items-center mb-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900 mr-4">
                    <principle.icon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {principle.title}
                  </h4>
                </div>
                <p className="text-gray-600 dark:text-gray-300">
                  {principle.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Data Collection */}
        <section className="mb-16">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Personal Information We Collect
              </h3>
              <ul className="space-y-3">
                {dataWeCollect.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600 dark:text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                How We Use Your Information
              </h3>
              <ul className="space-y-3">
                {howWeUseData.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600 dark:text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Your Rights */}
        <section className="mb-16">
          <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Your Rights Under POPIA
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <ul className="space-y-3">
                {yourRights.slice(0, 4).map((right, index) => (
                  <li key={index} className="flex items-start">
                    <Shield className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600 dark:text-gray-300">{right}</span>
                  </li>
                ))}
              </ul>
              <ul className="space-y-3">
                {yourRights.slice(4).map((right, index) => (
                  <li key={index} className="flex items-start">
                    <Shield className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600 dark:text-gray-300">{right}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900 dark:to-green-900 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Contact Our Information Officer
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  If you have any questions about how we handle your personal information or wish to exercise your rights under POPIA, please contact our Information Officer:
                </p>
                <div className="space-y-2">
                  <p className="text-gray-900 dark:text-white">
                    <strong>Email:</strong> privacy@aimfoundation.co.za
                  </p>
                  <p className="text-gray-900 dark:text-white">
                    <strong>Phone:</strong> +27715136694
                  </p>
                  <p className="text-gray-900 dark:text-white">
                    <strong>Address:</strong> AIM Foundation, Johannesburg
                  </p>
                </div>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  Information Regulator
                </h4>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  You also have the right to lodge a complaint with the Information Regulator:
                </p>
                <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                  <p>Website: www.justice.gov.za/inforeg/</p>
                  <p>Email: complaints.IR@justice.gov.za</p>
                  <p>Phone: 012 406 4818</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Updates */}
        <section className="text-center">
          <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Updates to This Policy
            </h3>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              We may update this POPIA compliance statement from time to time to reflect changes in our practices or applicable laws. We will notify you of any material changes by posting the updated statement on our website.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
              Last updated: January 2025
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default POPIA;
